import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiDownload, FiPlusSquare, FiMinusSquare, FiRefreshCw } from "react-icons/fi";
import ReactECharts from "echarts-for-react";
import axiosInstance from "../../../../Utils/axiosInstance";
import PLTable from './components/PLTable';
import FCTable from './components/FCTable';
import MetricCard from './components/MetricCard';
import CompareMonthsTable from './components/CompareMonthsTable';
import { formatValText, formatDashboardTitle, getPLCategoryOption, getBUNetPLOption, getFCPlanHeadOption, getBUNetFCOption } from './utils';


const ServiceMISDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dashboard, selectedProcess } = location.state || {};

  const [reportType, setReportType] = useState('P&L Report');
  const [period, setPeriod] = useState('May-2026');
  const [businessUnit, setBusinessUnit] = useState('All business units');
  const [searchDescription, setSearchDescription] = useState('');
  const [debouncedSearchDescription, setDebouncedSearchDescription] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchDescription(searchDescription);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchDescription]);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleResetFilters = () => {
    setReportType('P&L Report');
    setSearchDescription('');
    setDebouncedSearchDescription('');
    setBusinessUnit('All business units');
    setIsExpanded(false);
    
    const availablePeriods = apiFilterData?.find(item => item.Report_Type === 'P&L Report')?.months?.map(m => m.month_year || m.Month_Year) || [];
    const validPeriods = availablePeriods.filter(p => p !== 'Compare months');
    if (validPeriods.length > 0) {
      setPeriod(validPeriods[0]);
    } else {
      setPeriod('May-2026');
    }
  };

  const [apiFilterData, setApiFilterData] = useState([]);
    const [filtersLoading, setFiltersLoading] = useState(true);

  const [plApiData, setPlApiData] = useState({
    tableData: [],
    grandTotal: { kps: 0, op: 0, wh: 0, total: 0 },
    categoryData: [],
    buNetData: [],
    metrics: []
  });
  const [plLoading, setPlLoading] = useState(false);

  const [fcApiData, setFcApiData] = useState({
    tableData: [],
    grandTotal: { mKps: 0, mOp: 0, mWh: 0, mTotal: 0, yKps: 0, yOp: 0, yWh: 0 },
    planHeadData: [],
    buNetData: [],
    metrics: []
  });
  const [fcLoading, setFcLoading] = useState(false);


  useEffect(() => {
    if (filtersLoading) return;
    
    if (reportType === 'P&L Report') {
      const fetchPLData = async () => {
        try {
          setPlLoading(true);
          const response = await axiosInstance.post('service_mis/PL_Report', {
            month_year: period,
            Business_unit: businessUnit === 'All business units' ? '' : businessUnit,
            search: debouncedSearchDescription
          });
          
          if (response.data && response.data.status) {
            const { data, kpis, charts, businessUnitFixedCostByMonth, businessUnitNetPLByMonth } = response.data.items;
            
            // Build tree for table
            const map = {};
            let tree = [];
            let gt = { kps: 0, op: 0, wh: 0, total: 0 };
            
            if (period === 'Compare months') {
              tree = data || [];
            } else {
            (data || []).forEach(row => {
              const srNo = row.Sr_No || row.S_No;
              if (srNo == null || row.Description === 'Grand Total') {
                gt = {
                  kps: row.SE_KPS || 0,
                  op: row.SE_OP || 0,
                  wh: row.SE_WH || 0,
                  total: row.Grand_Total || 0
                };
                return;
              }
              map[srNo] = {
                id: srNo,
                description: row.Description,
                kps: row.SE_KPS || 0,
                op: row.SE_OP || 0,
                wh: row.SE_WH || 0,
                total: row.Grand_Total || 0,
                children: []
              };
            });
            (data || []).forEach(row => {
              const srNo = row.Sr_No || row.S_No;
              if (srNo == null || row.Description === 'Grand Total') return;
              const node = map[srNo];
              if (srNo && String(srNo).includes('.')) {
                const parentId = String(srNo).substring(0, String(srNo).lastIndexOf('.'));
                if (map[parentId]) {
                  map[parentId].children.push(node);
                } else {
                  tree.push(node);
                }
              } else {
                tree.push(node);
              }
            });
            }
            
            const metricIcons = {
              GrandTotal: { icon: '₹', iconBg: '#1e3a8a', borderColor: '#1e3a8a' },
              monthlyPaidOM: { icon: 'O', iconBg: '#ea580c', borderColor: '#ea580c' },
              monthlyKPS: { icon: 'K', iconBg: '#2563eb', borderColor: '#2563eb' },
              monthlyWarehouse: { icon: 'W', iconBg: '#16a34a', borderColor: '#16a34a' },
              plCategories: { icon: '#', iconBg: '#6d28d9', borderColor: '#6d28d9' },
              momChangeMonthly: { icon: '📈', iconBg: '#eab308', borderColor: '#eab308' }
            };
            
            const metrics = [];
            if (kpis) {
              metrics.push({ title: kpis.GrandTotal?.label?.toUpperCase() || 'NET P&L (GRAND TOTAL)', value: formatValText(kpis.GrandTotal?.value), trend: kpis.GrandTotal?.momChange > 0 ? 'up' : kpis.GrandTotal?.momChange < 0 ? 'down' : null, trendText: kpis.GrandTotal?.momChange ? Math.abs(kpis.GrandTotal.momChange).toFixed(1) + '%' : '', subtext: 'vs ' + kpis.GrandTotal?.previousMonth, ...metricIcons.GrandTotal });
              metrics.push({ title: kpis.monthlyPaidOM?.label?.toUpperCase() || 'SE OP - PAID O&M', value: formatValText(kpis.monthlyPaidOM?.value), trend: kpis.monthlyPaidOM?.momChange > 0 ? 'up' : kpis.monthlyPaidOM?.momChange < 0 ? 'down' : null, trendText: kpis.monthlyPaidOM?.momChange ? Math.abs(kpis.monthlyPaidOM.momChange).toFixed(1) + '%' : '', subtext: 'net', ...metricIcons.monthlyPaidOM });
              metrics.push({ title: kpis.monthlyKPS?.label?.toUpperCase() || 'SE KPS', value: formatValText(kpis.monthlyKPS?.value), trend: kpis.monthlyKPS?.momChange > 0 ? 'up' : kpis.monthlyKPS?.momChange < 0 ? 'down' : null, trendText: kpis.monthlyKPS?.momChange ? Math.abs(kpis.monthlyKPS.momChange).toFixed(1) + '%' : '', subtext: 'net', ...metricIcons.monthlyKPS });
              metrics.push({ title: kpis.monthlyWarehouse?.label?.toUpperCase() || 'SE WH - WAREHOUSE', value: formatValText(kpis.monthlyWarehouse?.value), trend: kpis.monthlyWarehouse?.momChange > 0 ? 'up' : kpis.monthlyWarehouse?.momChange < 0 ? 'down' : null, trendText: kpis.monthlyWarehouse?.momChange ? Math.abs(kpis.monthlyWarehouse.momChange).toFixed(1) + '%' : '', subtext: 'net', ...metricIcons.monthlyWarehouse });
              metrics.push({ title: kpis.plCategories?.label?.toUpperCase() || 'P&L CATEGORIES', value: String(kpis.plCategories?.value || 0), trend: null, trendText: '', subtext: kpis.plCategories?.subLabel || 'parent categories', ...metricIcons.plCategories });
              metrics.push({ title: kpis.momChangeMonthly?.label?.toUpperCase() || 'MOM CHANGE (NET)', value: formatValText(kpis.momChangeMonthly?.value), trend: kpis.momChangeMonthly?.value > 0 ? 'up' : kpis.momChangeMonthly?.value < 0 ? 'down' : null, trendText: '', subtext: 'vs ' + kpis.momChangeMonthly?.previousMonth, ...metricIcons.momChangeMonthly });
            }

            const catData = (charts?.fixedCostByPlanHead || []).map(d => {
              const val = d['Grand Total'] !== undefined ? d['Grand Total'] : (d.Grand_Total !== undefined ? d.Grand_Total : 0);
              return {
                value: val,
                name: d.Description,
                labelText: formatValText(val)
              };
            }).reverse();

            setPlApiData(prev => ({
              tableData: tree,
              grandTotal: gt,
              categoryData: catData,
              buNetData: charts?.businessUnitFixedCostByMonth || charts?.businessUnitNetPLByMonth || charts?.businessUnitNetPlByMonth || businessUnitFixedCostByMonth || businessUnitNetPLByMonth || [],
              metrics: metrics
            }));
          }
        } catch (error) {
          console.error('Error fetching PL Report:', error);
        } finally {
          setPlLoading(false);
        }
      };
      if (period) {
          fetchPLData();
        }
      } else if (reportType === 'FC Report') {
        const fetchFCData = async () => {
          try {
            setFcLoading(true);
            const response = await axiosInstance.post('service_mis/FC_Report', {
              month_year: period,
              Business_unit: businessUnit === 'All business units' ? '' : businessUnit,
              search: debouncedSearchDescription
            });
            
            if (response.data && response.data.status) {
              const { data, kpis, charts, businessUnitFixedCostByMonth } = response.data.items;
              
              let mappedMetrics = [];
              if (kpis) {
                mappedMetrics = [
                  { title: "MONTHLY FIXED COST (TOTAL)", value: formatValText(kpis.monthlyFixedCost?.value), trend: kpis.monthlyFixedCost?.momChange > 0 ? "up" : kpis.monthlyFixedCost?.momChange < 0 ? "down" : null, trendText: kpis.monthlyFixedCost?.momChange ? Math.abs(kpis.monthlyFixedCost.momChange).toFixed(1) + '%' : '', subtext: "vs " + kpis.monthlyFixedCost?.previousMonth, icon: "?", iconBg: "#1e3a8a", borderColor: "#1e3a8a" },
                  { title: "MONTHLY - PAID O&M", value: formatValText(kpis.monthlyPaidOM?.value), trend: kpis.monthlyPaidOM?.momChange > 0 ? "up" : kpis.monthlyPaidOM?.momChange < 0 ? "down" : null, trendText: kpis.monthlyPaidOM?.momChange ? Math.abs(kpis.monthlyPaidOM.momChange).toFixed(1) + '%' : '', subtext: "vs " + kpis.monthlyPaidOM?.previousMonth, icon: "O", iconBg: "#ea580c", borderColor: "#ea580c" },
                  { title: "MONTHLY - KPS", value: formatValText(kpis.monthlyKPS?.value), trend: kpis.monthlyKPS?.momChange > 0 ? "up" : kpis.monthlyKPS?.momChange < 0 ? "down" : null, trendText: kpis.monthlyKPS?.momChange ? Math.abs(kpis.monthlyKPS.momChange).toFixed(1) + '%' : '', subtext: "vs " + kpis.monthlyKPS?.previousMonth, icon: "K", iconBg: "#2563eb", borderColor: "#2563eb" },
                  { title: "MONTHLY - WAREHOUSE", value: formatValText(kpis.monthlyWarehouse?.value), trend: kpis.monthlyWarehouse?.momChange > 0 ? "up" : kpis.monthlyWarehouse?.momChange < 0 ? "down" : null, trendText: kpis.monthlyWarehouse?.momChange ? Math.abs(kpis.monthlyWarehouse.momChange).toFixed(1) + '%' : '', subtext: "vs " + kpis.monthlyWarehouse?.previousMonth, icon: "W", iconBg: "#16a34a", borderColor: "#16a34a" },
                  { title: "YTD ACTUALS (ALL BU)", value: formatValText(kpis.ytdActuals?.value), trend: null, trendText: "", subtext: kpis.ytdActuals?.subLabel, icon: "Y", iconBg: "#6d28d9", borderColor: "#6d28d9" },
                  { title: "MOM CHANGE (MONTHLY)", value: formatValText(kpis.momChangeMonthly?.value), trend: kpis.momChangeMonthly?.value > 0 ? "up" : kpis.momChangeMonthly?.value < 0 ? "down" : null, trendText: kpis.momChangeMonthly?.value ? Math.abs(kpis.momChangeMonthly.value).toFixed(1) + '%' : '', subtext: "vs " + kpis.momChangeMonthly?.previousMonth, icon: "?", iconBg: "#eab308", borderColor: "#eab308" }
                ];
              }

              const planHeadData = charts?.fixedCostByPlanHead || [];
              const buNetData = charts?.businessUnitFixedCostByMonth || charts?.business_unit_fixed_cost_by_month || businessUnitFixedCostByMonth || response.data.items?.business_unit_fixed_cost_by_month || [];
              
              let tree = [];
              let gt = { mKps: 0, mOp: 0, mWh: 0, mTotal: 0, yKps: 0, yOp: 0, yWh: 0 };
              
              if (period === 'Compare months') {
                tree = data || [];
              } else if (data && data.length > 0 && ('Sr_No' in data[0] || 'S_No' in data[0])) {
                const map = {};
                data.forEach(row => {
                  const srNo = row.Sr_No || row.S_No;
                  if (srNo == null || row.Description === 'Grand Total') {
                    gt = {
                      mKps: row.Monthly_Actuals_KPS || 0,
                      mOp: row.Monthly_Actuals_Paid_OM || 0,
                      mWh: row.Monthly_Actuals_Warehouse || 0,
                      mTotal: row.Monthly_Actuals_Grand_Total || 0,
                      yKps: row.YTD_Actuals_KPS || 0,
                      yOp: row.YTD_Actuals_Paid_OM || 0,
                      yWh: row.YTD_Actuals_Warehouse || 0
                    };
                    return;
                  }
                  map[srNo] = {
                    id: srNo,
                    description: row.Description,
                    mKps: row.Monthly_Actuals_KPS || 0,
                    mOp: row.Monthly_Actuals_Paid_OM || 0,
                    mWh: row.Monthly_Actuals_Warehouse || 0,
                    mTotal: row.Monthly_Actuals_Grand_Total || 0,
                    yKps: row.YTD_Actuals_KPS || 0,
                    yOp: row.YTD_Actuals_Paid_OM || 0,
                    yWh: row.YTD_Actuals_Warehouse || 0,
                    children: []
                  };
                });
                
                data.forEach(row => {
                  const srNo = row.Sr_No || row.S_No;
                  if (srNo == null || row.Description === 'Grand Total') return;
                  const node = map[srNo];
                  if (srNo && String(srNo).includes('.')) {
                    const parentId = String(srNo).substring(0, String(srNo).lastIndexOf('.'));
                    if (map[parentId]) {
                      map[parentId].children.push(node);
                    } else {
                      tree.push(node);
                    }
                  } else {
                    tree.push(node);
                  }
                });
              }
              
              setFcApiData(prev => ({
                ...prev,
                metrics: kpis ? mappedMetrics : prev.metrics,
                planHeadData: planHeadData,
                buNetData: buNetData,
                tableData: tree,
                grandTotal: gt
              }));
            }
          } catch (error) {
            console.error("Error fetching FC data:", error);
          } finally {
            setFcLoading(false);
          }
        };
        fetchFCData();
      }
    }, [reportType, period, businessUnit, debouncedSearchDescription, filtersLoading]);


  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setFiltersLoading(true);
        const response = await axiosInstance.get("service_mis/drop-down");
        if (response.data && response.data.status) {
          setApiFilterData(response.data.items?.monthYear || []);
        }
      } catch (error) {
        console.error("Error fetching MIS dropdowns:", error);
      } finally {
        setFiltersLoading(false);
      }
    };
    fetchFilters();
  }, []);

  const availableReportTypes = apiFilterData.length > 0 
    ? apiFilterData.map(d => d.Report_Type) 
    : ['P&L Report', 'FC Report'];
    
  const currentReportData = apiFilterData.find(d => d.Report_Type === reportType);
  
  const availablePeriods = currentReportData?.months?.map(m => m.month_year || m.Month_Year) 
    || ['Apr-2026', 'May-2026', 'Jun-2026', 'Compare months'];
    
  const availableBusinessUnits = currentReportData?.business_unit?.map(b => b.Business_unit) 
    || [];

  useEffect(() => {
    if (availablePeriods.length > 0 && !availablePeriods.includes(period)) {
      setPeriod(availablePeriods[0]);
    }
  }, [availablePeriods, period]);

  useEffect(() => {
    if (availableBusinessUnits.length > 0 && !availableBusinessUnits.includes(businessUnit)) {
      setBusinessUnit(availableBusinessUnits[0]);
    } else if (availableBusinessUnits.length === 0 && businessUnit !== 'All business units') {
      setBusinessUnit('All business units');
    }
  }, [availableBusinessUnits, businessUnit]);

  if (!location.state) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        Open this page from the dashboard list so the selected dashboard context
        is available.
      </div>
    );
  }

  const title = formatDashboardTitle(dashboard?.eda_name);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">
            Process: {formatDashboardTitle(selectedProcess)} | Status:{" "}
            {dashboard?.edaStatus || "Available"}
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300"
        >
          <FiArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* Filter Section */}
      <div className="bg-[#f8f9fa] border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex flex-wrap items-end gap-5">
          
          {/* REPORT */}
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-bold text-slate-500 uppercase tracking-wide">Report</label>
            <div className="inline-flex rounded-lg p-1 bg-slate-100 border border-slate-200 h-[44px]">
              {availableReportTypes.map(item => (
                <button
                  key={item}
                  onClick={() => setReportType(item)}
                  className={`px-4 py-1 text-[15px] font-medium rounded-md transition-colors ${
                    reportType === item ? 'bg-white text-[#0060D0] shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          {/* PERIOD */}
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-bold text-slate-500 uppercase tracking-wide">Period</label>
            <div className="inline-flex rounded-lg p-1 bg-slate-100 border border-slate-200 h-[44px]">
              {availablePeriods.map(item => (
                <button
                  key={item}
                  onClick={() => setPeriod(item)}
                  className={`px-4 py-1 text-[15px] font-medium rounded-md transition-colors ${
                    period === item ? 'bg-white text-[#0060D0] shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          {/* BUSINESS UNIT FOCUS */}
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-bold text-slate-500 uppercase tracking-wide">Business Unit Focus</label>
            <select 
              value={businessUnit}
              onChange={(e) => setBusinessUnit(e.target.value)}
              className="h-[44px] px-4 py-1 text-[15px] text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-[#0060D0] focus:ring-1 focus:ring-[#0060D0] cursor-pointer disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
              disabled={availableBusinessUnits.length === 0}
            >
              {availableBusinessUnits.length > 0 ? (
                availableBusinessUnits.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))
              ) : (
                <option value="All business units">All business units (N/A)</option>
              )}
            </select>
          </div>

          {/* SEARCH DESCRIPTION */}
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-bold text-slate-500 uppercase tracking-wide">Search Description</label>
            <input 
              type="text"
              placeholder="e.g. Manpower, Travel, Re..."
              value={searchDescription}
              onChange={(e) => setSearchDescription(e.target.value)}
              className="h-[44px] w-[240px] px-4 py-1 text-[15px] text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-[#0060D0] focus:ring-1 focus:ring-[#0060D0] placeholder-slate-400"
            />
          </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 ml-2">
              {/* Invisible label for vertical alignment with other filters */}
              <label className="text-[13px] font-bold text-transparent select-none uppercase tracking-wide">Actions</label>
              <div className="flex gap-3">
                <button 
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-[44px] inline-flex items-center justify-center gap-2 px-5 py-1 text-[15px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {isExpanded ? <FiMinusSquare className="w-[18px] h-[18px]" /> : <FiPlusSquare className="w-[18px] h-[18px]" />}
                  {isExpanded ? 'Collapse all' : 'Expand all'}
                </button>

                <button 
                  onClick={handleResetFilters}
                  className="h-[44px] inline-flex items-center justify-center gap-2 px-5 py-1 text-[15px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <FiRefreshCw className="w-[16px] h-[16px]" />
                  Reset filters
                </button>
              </div>
            </div>

        </div>
      </div>
    
      {((reportType === 'P&L Report' ? plLoading : fcLoading) || filtersLoading) ? (
        <div className="flex justify-center items-center h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0060D0]"></div>
        </div>
      ) : (
        <>
          {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        {(reportType === 'P&L Report' ? plApiData.metrics : fcApiData.metrics).map((metric, idx) => (
          <MetricCard key={idx} {...metric} />
        ))}
      </div>

      {reportType === 'P&L Report' && (
        <>
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Chart 1: P&L by Category */}
            <div className="bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-slate-200 p-5 flex flex-col">
              <div>
                <h3 className="text-[16px] font-bold text-slate-800">P&L by Category</h3>
              </div>
              <div className="flex-1 mt-6">
                <ReactECharts notMerge={true} option={getPLCategoryOption(plApiData.categoryData)} style={{ height: '320px', width: '100%' }} opts={{ renderer: 'svg' }} />
              </div>
              <div className="flex items-center gap-4 mt-2 text-[14px] text-slate-500 font-medium">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#3b82f6] rounded-[2px]"></div> Debit - cost (+)</div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#22c55e] rounded-[2px]"></div> Credit - income (-)</div>
              </div>
            </div>


            {/* Chart 2: BU net P&L */}
            <div className="bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-slate-200 p-5 flex flex-col">
              <div>
                <h3 className="text-[16px] font-bold text-slate-800">Business-unit net P&L by month</h3>
              </div>
              <div className="flex-1 mt-6">
                <ReactECharts notMerge={true} option={getBUNetPLOption(plApiData.buNetData)} style={{ height: '340px', width: '100%' }} opts={{ renderer: 'svg' }} />
              </div>
            </div>

           
          </div>

          {/* Details Table */}
          <div className="mt-10">
            {period === 'Compare months' ? <CompareMonthsTable data={plApiData.tableData} /> : <PLTable data={plApiData.tableData} grandTotal={plApiData.grandTotal} isExpanded={isExpanded} businessUnit={businessUnit} />}
          </div>
        </>
      )}

      {reportType === 'FC Report' && (
        <>
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Chart 1: Fixed Cost by Plan Head */}
            <div className="bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-slate-200 p-5 flex flex-col">
              <div>
                <h3 className="text-[16px] font-bold text-slate-800">Fixed Cost by Plan Head</h3>
              </div>
              <div className="flex-1 mt-6">
                <ReactECharts option={getFCPlanHeadOption(fcApiData.planHeadData)} style={{ height: '420px', width: '100%' }} opts={{ renderer: 'svg' }} />
              </div>
              <div className="flex items-center gap-4 mt-2 text-[14px] text-slate-500 font-medium">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#3b82f6] rounded-[2px]"></div> Debit - cost (+)</div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#22c55e] rounded-[2px]"></div> Credit - income (-)</div>
              </div>
            </div>

             {/* Chart 2: BU net FC */}
            <div className="bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-slate-200 p-5 flex flex-col">
              <div>
                <h3 className="text-[16px] font-bold text-slate-800">Business-unit fixed cost by month</h3>
              </div>
              <div className="flex-1 mt-6">
                <ReactECharts notMerge={true} option={getBUNetFCOption(fcApiData.buNetData)} style={{ height: '440px', width: '100%' }} opts={{ renderer: 'svg' }} />
              </div>
            </div>

            
          </div>

          {/* Details Table */}
          <div className="mt-10">
            {period === 'Compare months' ? <CompareMonthsTable data={fcApiData.tableData} /> : <FCTable data={fcApiData.tableData} grandTotal={fcApiData.grandTotal} isExpanded={isExpanded} businessUnit={businessUnit} />}
          </div>
        </>
      )}

       {/* Export Buttons */}
      {dashboard?.Main_EDA_Files && dashboard.Main_EDA_Files.length > 0 && (
        <div className="flex justify-end gap-3 pt-2 pb-5">
          {dashboard.Main_EDA_Files.map((file, idx) => {
            const link = file.xlsLink || file.csvLink;
            if (!link) return null;
            return (
              <a
                key={idx}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-[#0060D0] px-4 py-2 text-sm font-semibold !text-white shadow-sm transition hover:bg-[#0050b0]"
              >
                <FiDownload className="h-4 w-4 shrink-0" />
                Download Consolidated Report
              </a>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default ServiceMISDashboard;





